import React, { useEffect, useState } from "react";
import { notifyError, notifySuccess } from "../../utils/tostfy";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { getApiErrorMessage, handleApiCall } from "../../apis";
import { endpoint } from "../../utils/endpoints";
import {
  formatEmployeeIdForApi,
  normalizeEmployeeIdDigits,
  sanitizeEmployeeIdInput,
} from "../../utils/employeeId";
import downArrow from "../../assets/image/down-arrow.svg";
import PhoneNumberField from "../../Components/PhoneNumberField/PhoneNumberField";
import { FIXED_COUNTRY_CODE } from "../../utils/phone";

const selectBaseClass =
  "md:h-[54px] h-[44px] w-full appearance-none rounded-3 border-[1px] bg-transparent px-3 pr-10 text-[15px] outline-none disabled:cursor-not-allowed disabled:opacity-70";

const SelectChevron = ({ className = "" }) => (
  <img
    src={downArrow}
    alt=""
    aria-hidden
    className={`pointer-events-none absolute top-1/2 h-[7px] w-[7px] -translate-y-1/2 shrink-0 ${className}`}
  />
);

const FormSelect = ({ className = "", wrapperClassName = "", hasError = false, children, ...props }) => (
  <div className={`relative ${wrapperClassName}`}>
    <select
      className={`${selectBaseClass} border-[#0B3E64] ${hasError ? "border-red-500" : ""} ${className}`}
      {...props}
    >
      {children}
    </select>
    <SelectChevron className="right-3" />
  </div>
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_IN_RE = /^[6-9]\d{9}$/;

const initialErrors = {
  employee_id: "",
  full_name: "",
  email: "",
  mobile_number: "",
  mipl_location_id: "",
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value.data)) return value.data;
  return [];
};

const mapLocation = (item) => ({
  id: item?._id || item?.id || "",
  name: item?.name || item?.location_name || "Unknown Location",
});

const AddNewAccount = () => {
  const navigate = useNavigate();
  const { setSubheading } = useOutletContext();
  const [miplLocations, setMiplLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [employeeId, setEmployeeId] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [miplLocationId, setMiplLocationId] = useState("");

  const [errors, setErrors] = useState(initialErrors);

  useEffect(() => {
    setSubheading("Add new Accountant");
    return () => {
      setSubheading("");
    };
  }, [setSubheading]);

  const fetchMiplLocations = async () => {
    setLoadingLocations(true);
    try {
      const response = await handleApiCall("get", endpoint.listOfAlllocation, {});
      if (response?.data?.code === 200) {
        const d = response?.data?.data;
        const raw = Array.isArray(d) ? d : toArray(d?.location_data);
        const mapped = raw.map(mapLocation).filter((x) => x.id && x.name);
        setMiplLocations(mapped);
      } else {
        const errMsg = getApiErrorMessage(response, "Failed to load MIPL locations");
        if (errMsg) notifyError(errMsg);
        setMiplLocations([]);
      }
    } catch {
      notifyError("Failed to load MIPL locations");
      setMiplLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  };

  useEffect(() => {
    fetchMiplLocations();
  }, []);

  const clearFieldError = (key) => {
    setErrors((prev) => (prev[key] ? { ...prev, [key]: "" } : prev));
  };

  const validate = () => {
    const next = { ...initialErrors };
    const id = normalizeEmployeeIdDigits(employeeId);
    const name = (fullName || "").trim();
    const em = (email || "").trim();
    const mobile = (mobileNumber || "").replace(/\D/g, "");
    const loc = (miplLocationId || "").trim();

    if (!id) next.employee_id = "Employee ID is required";

    if (!name) next.full_name = "Name is required";
    else if (name.length < 2) next.full_name = "Name must be at least 2 characters";
    else if (name.length > 100) next.full_name = "Name is too long";
    else if (!/^[a-zA-Z][a-zA-Z\s.'-]*$/.test(name)) {
      next.full_name = "Enter a valid name";
    }

    if (!em) next.email = "Email is required";
    else if (!EMAIL_RE.test(em)) next.email = "Enter a valid email address";

    if (!mobile) next.mobile_number = "Phone number is required";
    else if (!MOBILE_IN_RE.test(mobile)) {
      next.mobile_number = "Enter a valid 10-digit Indian mobile number";
    }

    if (!loc) next.mipl_location_id = "Select a MIPL location";

    setErrors(next);
    return !Object.values(next).some(Boolean);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;

    setSubmitting(true);
    try {
      const digits = normalizeEmployeeIdDigits(employeeId);
      const payload = {
        employee_id: formatEmployeeIdForApi(digits),
        full_name: (fullName || "").trim(),
        email: (email || "").trim().toLowerCase(),
        mobile_number: (mobileNumber || "").replace(/\D/g, ""),
        country_code: FIXED_COUNTRY_CODE,
        mipl_location_id: (miplLocationId || "").trim(),
      };

      const response = await handleApiCall("post", endpoint.addAccountant, payload);
      if (response?.data?.code === 200) {
        notifySuccess(response?.data?.message || "Accountant added successfully");
        setTimeout(() => {
          navigate("/accountant-management");
        }, 1500);
        return;
      }

      if (response?.__apiAlertHandled || response?.__sessionExpired) return;

      const errMsg = getApiErrorMessage(response, "Failed to add accountant");
      if (errMsg) notifyError(errMsg);
    } catch {
      notifyError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <form
        onSubmit={onSubmit}
        className="row col-md-12 bg-white mx-0 mt-6 px-2 py-3 md:p-4 shadow-sm font-UbuntuRegular g-3"
        noValidate
      >
        <div className="col-lg-3 col-md-6 mb-0 min-w-0 mt-0 md:!mt-[1rem]">
          <label className="font-UbuntuMedium">Employee ID</label>
          <div
            className={`mt-2 flex w-full min-w-0 items-stretch overflow-hidden rounded-3 border-[1px] ${
              errors.employee_id ? "border-red-500" : "border-[#0B3E64]"
            }`}
          >
            <span
              className="flex shrink-0 items-center border-r border-[#0B3E64]/25 bg-[#f5f8fa] px-3 font-UbuntuMedium text-[#0B3E64]"
              aria-hidden
            >
              MI
            </span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              maxLength={5}
              placeholder="00000"
              className="min-w-0 flex-1 border-0 bg-transparent px-3 py-0 outline-none md:h-[54px] h-[44px]"
              value={employeeId}
              onChange={(e) => {
                setEmployeeId(sanitizeEmployeeIdInput(e.target.value));
                clearFieldError("employee_id");
              }}
              onBlur={() => {
                const normalized = normalizeEmployeeIdDigits(employeeId);
                if (normalized !== employeeId) setEmployeeId(normalized);
              }}
              disabled={submitting}
              aria-label="Employee ID number (5 digits)"
            />
          </div>
          {errors.employee_id ? (
            <p className="text-red-500 text-sm mt-1 mb-0">{errors.employee_id}</p>
          ) : null}
        </div>
        <div className="col-lg-3 col-md-6 mb-0 min-w-0">
          <label className="font-UbuntuMedium">Accountant Name</label>
          <input
            type="text"
            className={`w-full h-[44px] md:h-[54px] border-[1px] border-[#0B3E64] rounded-3 bg-transparent mt-2 outline-none px-3  ${
              errors.full_name ? "border-red-500" : ""
            }`}
            value={fullName}
            onChange={(e) => {
              setFullName(e.target.value);
              clearFieldError("full_name");
            }}
            autoComplete="name"
            disabled={submitting}
          />
          {errors.full_name ? (
            <p className="text-red-500 text-sm mt-1 mb-0">{errors.full_name}</p>
          ) : null}
        </div>
        <div className="col-lg-3 col-md-6 mb-0 min-w-0">
          <label className="font-UbuntuMedium">Email</label>
          <input
            type="email"
            className={`w-full h-[44px] md:h-[54px] border-[1px] border-[#0B3E64] rounded-3 bg-transparent mt-2 outline-none px-3 ${
              errors.email ? "border-red-500" : ""
            }`}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              clearFieldError("email");
            }}
            autoComplete="email"
            disabled={submitting}
          />
          {errors.email ? <p className="text-red-500 text-sm mt-1 mb-0">{errors.email}</p> : null}
        </div>
        <div className="col-lg-3 col-md-6 mb-0 min-w-0">
          <label className="font-UbuntuMedium">Phone Number</label>
          <div className="mt-2">
            <PhoneNumberField
              value={mobileNumber}
              onChange={(v) => {
                setMobileNumber(v);
                clearFieldError("mobile_number");
              }}
              disabled={submitting}
              hasError={!!errors.mobile_number}
            />
          </div>
          {errors.mobile_number ? (
            <p className="text-red-500 text-sm mt-1 mb-0">{errors.mobile_number}</p>
          ) : null}
        </div>
        <div className="col-lg-3 col-md-6 mb-0 min-w-0">
          <label className="font-UbuntuMedium">Select MIPL Location</label>
          <FormSelect
            wrapperClassName="mt-2 max-w-full"
            hasError={!!errors.mipl_location_id}
            value={miplLocationId}
            onChange={(e) => {
              setMiplLocationId(e.target.value);
              clearFieldError("mipl_location_id");
            }}
            disabled={submitting || loadingLocations}
          >
            <option value="">{loadingLocations ? "Loading locations…" : "Select location"}</option>
            {miplLocations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </FormSelect>
          {errors.mipl_location_id ? (
            <p className="text-red-500 text-sm mt-1 mb-0">{errors.mipl_location_id}</p>
          ) : null}
        </div>
        <div className="col-md-12 mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 pt-2">
          <Link
            to="/accountant-management"
            className="btn btn-primary"
            onClick={(e) => submitting && e.preventDefault()}
          >
            Cancel
          </Link>
          <button type="submit" className="btn btn-danger md:min-w-32" disabled={submitting}>
            {submitting ? "Adding…" : "Add"}
          </button>
        </div>
      </form>
    </>
  );
};

export default AddNewAccount;
